import type { AxiosInstance } from 'axios';
import { BILLING_ENDPOINTS as E } from '../endpoints';
import { BILLING_KEY_CHARGE_TIMEOUT_MS } from './timeouts';
import type {
  AnnualTerminationPreview,
  ChangePaymentMethodRequest,
  CompleteBillingSubscriptionBatchRequest,
  CompleteBillingSubscriptionBatchResult,
  CompleteBillingSubscriptionRequest,
  CompleteBillingSubscriptionResult,
  EarlyTerminationPreview,
  ExtendCommitmentRequest,
  LegacyTermProduct,
  MyActiveSubscription,
  MyLicense,
  ReactivateSubscriptionRequest,
  ReactivationPreview,
  SeatImpactPreview,
  SubscriptionView,
  WithdrawalPreview,
  WithdrawalResult,
} from '../types/subscription.type';

/**
 * 구독·라이선스 호출부 (본인 스코프). 인증 필요 — 본인 판정은 서버가
 * Gateway 주입 x-user-id 로 수행한다.
 *
 * business.api 와 같은 이유로 팩토리다 — 인스턴스만 주입받고 호출부는 한 벌만 둔다.
 *
 * @example
 * export const subscriptionApi = createSubscriptionApi(apiClient);
 */
export function createSubscriptionApi(apiClient: AxiosInstance) {
  return {

    /**
     * 아바타 메뉴(모달)용 개인 구독 1건 (없으면 null).
     * ACTIVE 외 PAST_DUE(연체)·PAUSED(일시정지)도 포함해 재활성화 진입을 노출한다.
     */
    myActive: () =>
      apiClient
        .get<MyActiveSubscription | null>(E.myDisplaySubscription)
        .then((r) => r.data),

    /** 본인에게 귀속/배정된 활성 라이선스 목록. */
    myActiveLicenses: () =>
      apiClient.get<MyLicense[]>(E.myActiveLicenses).then((r) => r.data),

    /** 자동결제 구독 완성(카드등록 성공 후). 개인=orgId 생략, 기업=orgId+seatCount. */
    completeBilling: (body: CompleteBillingSubscriptionRequest) =>
      apiClient
        .post<CompleteBillingSubscriptionResult>(
          E.completeBillingSubscription,
          body,
          { timeout: BILLING_KEY_CHARGE_TIMEOUT_MS },
        )
        .then((r) => r.data),

    /** 단품 다중 구매 완성(카드등록 성공 후). 항목별 결과가 온다. */
    completeBillingBatch: (body: CompleteBillingSubscriptionBatchRequest) =>
      apiClient
        .post<CompleteBillingSubscriptionBatchResult>(
          E.completeBillingSubscriptionBatch,
          body,
          { timeout: BILLING_KEY_CHARGE_TIMEOUT_MS },
        )
        .then((r) => r.data),

    /** 본인(개인 소유)이 이미 보유한 상품 id. */
    myOwnedProducts: () =>
      apiClient.get<string[]>(E.myOwnedProducts).then((r) => r.data),

    /** 본인의 구버전 이관 기간제 구독 상품(영구 폴백 제외). */
    myLegacyTerms: () =>
      apiClient.get<LegacyTermProduct[]>(E.myLegacyTerms).then((r) => r.data),

    /** 재활성화 프리뷰(비청구) — 청구 예정 금액·결제 가능 여부. */
    reactivatePreview: (id: string) =>
      apiClient
        .get<ReactivationPreview>(E.reactivatePreview(id))
        .then((r) => r.data),

    /** 결제수단(카드) 변경 — 카드 재등록 성공 후 authKey/customerKey 전달. */
    changePaymentMethod: (id: string, body: ChangePaymentMethodRequest) =>
      apiClient
        .patch<{ message: string }>(E.changeSubscriptionCard(id), body, {
          timeout: 65000,
        })
        .then((r) => r.data),

    /** 일시정지(PAUSED) 구독 재활성화. 새 카드면 authKey/customerKey, 기존 카드면 생략. */
    reactivate: (id: string, body?: ReactivateSubscriptionRequest) =>
      apiClient
        .post<SubscriptionView>(E.reactivateSubscription(id), body ?? {}, {
          timeout: BILLING_KEY_CHARGE_TIMEOUT_MS,
        })
        .then((r) => r.data),

    // ─── 약정 중도해지 (§7.3/§7.8) ─────────────────────────────
    /** 중도해지 정산 프리뷰(비청구). 예상 위약금·잔여 개월. */
    earlyTerminationPreview: (id: string) =>
      apiClient
        .get<EarlyTerminationPreview>(E.earlyTerminationPreview(id))
        .then((r) => r.data),

    /** 중도해지 요청 접수 → cancel_requested(무약정은 주기말 해지). */
    requestCancellation: (id: string, memo?: string) =>
      apiClient
        .post<SubscriptionView>(E.cancelRequest(id), { memo })
        .then((r) => r.data),

    /**
     * 해지 영향도(dry-run) — 좌석이 살아있는 구독으로 이관되는지/끊기는지.
     * 기업 구독 전용 의미(개인 구독은 전부 0).
     */
    seatImpact: (id: string) =>
      apiClient.get<SeatImpactPreview>(E.seatImpact(id)).then((r) => r.data),

    /** 해지 철회 — 주기말 해지 예약/중도해지 요청 되돌리기. 확정 해지는 400. */
    revokeCancellation: (id: string) =>
      apiClient
        .post<SubscriptionView>(E.revokeCancelRequest(id))
        .then((r) => r.data),

    // ─── 청약철회 (§6.1) ───────────────────────────────────────
    //   개인 구독·미사용·7일 이내에만 열린다. 기업 구독과 예외 심사는 Admin 재량 경로다.

    /** 청약철회 프리뷰(비청구). 7일 이내 개인 구독일 때만 호출한다. */
    withdrawalPreview: (id: string) =>
      apiClient
        .get<WithdrawalPreview>(E.withdrawalPreview(id))
        .then((r) => r.data),

    /**
     * 청약철회 실행 — 전액 환불 + 즉시 이용 종료.
     * 바디를 보내지 않는다. 환불 비율은 서버가 정한다.
     */
    requestWithdrawal: (id: string) =>
      apiClient
        .post<WithdrawalResult>(E.withdrawal(id), undefined, { timeout: 65000 })
        .then((r) => r.data),

    // ─── 개인 연간 중도해지 (환불·해지 정책 제5조 ④) ─────────────
    //   신청만 한다. 환불·종료는 운영자 승인(3영업일 내) 때 실행된다.

    /** 중도해지 환불 프리뷰(비청구) — 이용 개월·월 정가·환불 예정액. */
    annualTerminationPreview: (id: string) =>
      apiClient
        .get<AnnualTerminationPreview>(E.annualTerminationPreview(id))
        .then((r) => r.data),

    /** 중도해지 신청 접수 → cancel_requested. */
    requestAnnualTermination: (id: string, memo?: string) =>
      apiClient
        .post<SubscriptionView>(E.annualTermination(id), { memo })
        .then((r) => r.data),

    // ─── 약정 연장 (§7.4) ──────────────────────────────────────
    extendCommitment: (id: string, body: ExtendCommitmentRequest) =>
      apiClient
        .post<SubscriptionView>(E.extendCommitment(id), body)
        .then((r) => r.data),
  };
}

export type SubscriptionApi = ReturnType<typeof createSubscriptionApi>;
